import { XMarkIcon } from '@heroicons/react/24/outline';
import { Bug } from 'lucide-react';
import { memo, useState } from 'react';

import { ConditionalRender } from '@/components/ConditionalRender';
import { useOpenEntityDrawer } from '@/hooks/useOpenEntityDrawer';
import { NodeHandles } from '@/sections/attacks/attackPaths/components/nodes/NodeHandles';
import { VulnerabilityModal } from '@/sections/attacks/attackPaths/components/wizards/VulnerabilityModal';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import type { CustomVulnerabilityNode } from '@/sections/attacks/attackPaths/types';
import { parseKeys } from '@/sections/SearchByType';
import { cn } from '@/utils/classname';

/**
 * Severity colors based on priority value.
 * Priority values: 0=Critical, 10=High, 20=Medium, 30=Low, 40=Info, 50=Exposure
 * Uses Chariot design tokens for consistency with the design system.
 */
const severityColors: Record<
  number,
  { bg: string; border: string; text: string }
> = {
  0: {
    bg: 'bg-chariot-status-critical/20',
    border: 'border-chariot-stroke-critical',
    text: 'text-chariot-stroke-critical',
  },
  10: {
    bg: 'bg-chariot-status-high/20',
    border: 'border-chariot-stroke-high',
    text: 'text-chariot-stroke-high',
  },
  20: {
    bg: 'bg-chariot-status-medium/20',
    border: 'border-chariot-stroke-medium',
    text: 'text-chariot-stroke-medium',
  },
  30: {
    bg: 'bg-chariot-status-low/20',
    border: 'border-chariot-stroke-low',
    text: 'text-chariot-stroke-low',
  },
  40: {
    bg: 'bg-chariot-status-info/20',
    border: 'border-chariot-stroke-info',
    text: 'text-chariot-stroke-info',
  },
  50: {
    bg: 'bg-chariot-status-info/20',
    border: 'border-chariot-stroke-info',
    text: 'text-chariot-stroke-info',
  },
};

/**
 * Default colors for info/unknown severity
 */
const defaultColors = {
  bg: 'bg-chariot-status-info/20',
  border: 'border-chariot-stroke-info',
  text: 'text-chariot-stroke-info',
};

interface VulnerabilityNodeComponentProps {
  data: CustomVulnerabilityNode['data'];
  id: string;
}

/**
 * Vulnerability node component for attack path visualization.
 *
 * Features:
 * - Display Chariot vulnerability (risk) or custom non-Chariot vulnerability
 * - Edit mode: Opens modal for editing via VulnerabilityModal
 * - Severity-based coloring
 * - Link to risk drawer in view mode
 * - Handles for connecting edges
 */
function VulnerabilityNodeComponent({
  data,
  id,
}: VulnerabilityNodeComponentProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { removeNode, isEditMode } = useAttackState();
  const { openDrawerByKey } = useOpenEntityDrawer();

  // Parse risk key to extract name for display
  const { name: riskName } =
    !data.type && data.key ? parseKeys.riskKey(data.key) : { name: '' };

  // Determine if this is a valid Chariot risk key
  const isRiskKey = !data.type && data.key?.startsWith('#risk#');

  // Create node object for edit modal
  const nodeForEdit: CustomVulnerabilityNode = {
    id,
    type: 'vulnerability',
    position: { x: 0, y: 0 },
    data,
  };

  /**
   * Handle click on node in edit mode - open edit modal
   */
  const handleEditClick = () => {
    if (isEditMode) {
      setEditModalOpen(true);
    }
  };

  // Determine display name
  const displayName =
    data.name || riskName || data.key || 'Select vulnerability';

  // Get severity colors based on priority
  const colors = severityColors[data.priority ?? 40] || defaultColors;

  return (
    <>
      <ConditionalRender
        condition={isEditMode}
        conditionalWrapper={children => (
          <div
            role="button"
            tabIndex={0}
            onClick={handleEditClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleEditClick();
              }
            }}
            className="cursor-pointer"
            aria-label="Edit vulnerability node"
          >
            {children}
          </div>
        )}
        elseWrapper={children => (
          <ConditionalRender
            condition={Boolean(isRiskKey)}
            conditionalWrapper={innerChildren => (
              <button
                type="button"
                className="cursor-pointer transition-opacity hover:opacity-80"
                onClick={() =>
                  data.key && openDrawerByKey(data.key, 'vulnerability')
                }
                aria-label="View vulnerability details"
              >
                {innerChildren}
              </button>
            )}
          >
            {children}
          </ConditionalRender>
        )}
      >
        <div className="relative flex w-[72px] flex-col items-center">
          {/* Circular node container - 72x72px */}
          <div
            className={cn(
              'relative flex size-[72px] items-center justify-center rounded-full border-2',
              colors.bg,
              colors.border
            )}
          >
            <Bug className={cn('size-8', colors.text)} />
            {isEditMode && (
              <button
                className="bg-chariot-status-critical hover:bg-chariot-status-critical/80 absolute -top-1 -right-1 rounded-full p-[2px]"
                onClick={event => {
                  event.stopPropagation();
                  removeNode(id);
                }}
                aria-label="Remove node"
              >
                <XMarkIcon className="text-chariot-text-primary size-3 stroke-[4px]" />
              </button>
            )}
          </div>
          {/* Vulnerability name label below */}
          <div className="mt-2 w-max min-w-full text-center text-sm font-medium wrap-break-word">
            {displayName}
          </div>
        </div>
      </ConditionalRender>

      <NodeHandles isEditMode={isEditMode} />

      {/* Edit Modal */}
      <VulnerabilityModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        mode="edit"
        initialNode={nodeForEdit}
      />
    </>
  );
}

export const VulnerabilityNode = memo(VulnerabilityNodeComponent);
